import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useApp } from "../contexts/AppContext";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { 
  LayoutDashboard, Users, Building2, Calendar, Wallet, 
  Settings, LogOut, Menu, X, ChevronDown, User, CreditCard, FileText, EyeOff, Shield
} from "lucide-react";
import { cn, API_URL } from "../lib/utils";

const resolveUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${API_URL}${url}`;
};

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, adminOnly: true },
  { path: "/employees", label: "Dipendenti", icon: Users, adminOnly: false, permission: "employees_view" },
  { path: "/companies", label: "Aziende", icon: Building2, adminOnly: false, permission: "companies_view" },
  { path: "/schedules", label: "Orari", icon: Calendar, adminOnly: false, permission: "schedules_view" },
  { path: "/payrolls", label: "Stipendi", icon: Wallet, adminOnly: false, permission: "payrolls_view" },
  { path: "/loans", label: "Prestiti", icon: CreditCard, adminOnly: true },
  { path: "/contracts", label: "Scadenze", icon: FileText, adminOnly: true },
  { path: "/ghost", label: "Indennità", icon: EyeOff, adminOnly: true },
  { path: "/compliance", label: "Compliance", icon: Shield, adminOnly: true },
];

const adminItems = [
  { path: "/admin", label: "Admin", icon: Settings, permission: null },
];

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { settings } = useApp();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const permissions = user?.permissions || {};

  const filteredNavItems = isAdmin
    ? navItems
    : navItems.filter(item => {
        if (item.adminOnly) return false;
        if (item.permission && permissions[item.permission] === false) return false;
        return true;
      });

  const allNavItems = isAdmin ? [...filteredNavItems, ...adminItems] : filteredNavItems;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 border-b border-[#27272A] z-50 flex items-center justify-between px-4" style={{ backgroundColor: "var(--primary-color, #09090B)" }}>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white hover:bg-[#27272A]"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
        <div className="flex items-center gap-2">
          {settings?.logo_url ? (
            <img src={resolveUrl(settings.logo_url)} alt="Logo" className="w-6 h-6 object-contain" />
          ) : (
            <Building2 className="w-5 h-5 text-white" />
          )}
          <span className="font-outfit font-semibold text-white text-sm">
            {settings?.app_name || "Gestionale HR"}
          </span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="text-white hover:bg-[#27272A]">
              <User className="w-5 h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleLogout} className="font-ibm-plex">
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed top-0 left-0 bottom-0 w-64 border-r border-[#27272A] z-40 transition-transform duration-200",
          "lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
        style={{ backgroundColor: "var(--primary-color, #09090B)" }}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-14 flex items-center gap-3 px-4 border-b border-[#27272A]">
            {settings?.logo_url ? (
              <img src={resolveUrl(settings.logo_url)} alt="Logo" className="w-8 h-8 object-contain" />
            ) : (
              <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#09090B]" />
              </div>
            )}
            <span className="font-outfit font-semibold text-white">
              {settings?.app_name || "Gestionale HR"}
            </span>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="space-y-1 px-3">
              {allNavItems.map(item => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.path.replace("/", "")}`}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-ibm-plex transition-colors",
                      isActive 
                        ? "bg-white text-[#09090B] font-medium" 
                        : "text-[#A1A1AA] hover:text-white hover:bg-[#27272A]"
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User */}
          <div className="border-t border-[#27272A] p-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="w-full justify-between text-white hover:bg-[#27272A] font-ibm-plex"
                  data-testid="user-menu-trigger"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-[#27272A] rounded-full flex items-center justify-center">
                      <User className="w-4 h-4" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium truncate max-w-[120px]">{user?.name}</p>
                      <p className="text-xs text-[#A1A1AA] capitalize">{user?.role}</p>
                    </div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-[#A1A1AA]" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem className="font-ibm-plex">
                  <User className="w-4 h-4 mr-2" />
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="font-ibm-plex text-red-500">
                  <LogOut className="w-4 h-4 mr-2" />
                  Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen pt-14 lg:pt-0">
        <div className="p-6 lg:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
